/**
 * Voucher Pembayaran print template - shared by Invoices and PaymentRequests
 * so the AP and Approval screens always print identical numbers.
 */
import { escapeHtml, printHtml, printWhenReady, writePrintDocument } from './printHelper';
import { invoiceAmounts } from './invoiceAmounts';
import { signatory } from './signatures';
import { batchTotals } from './voucherBatch';

const VOUCHER_STYLE = `
  body { font-family: Arial, sans-serif; padding: 25px 30px; font-size: 9pt; color: #000; }
  h1 { text-align: center; font-size: 14pt; font-weight: bold; text-decoration: underline; margin: 0 0 20px 0; }
  .top-info { display: flex; justify-content: space-between; margin-bottom: 5px; font-size: 9pt; }
  .top-info span.label { font-weight: bold; }
  .top-info .line { margin-bottom: 3px; }
  /* Ruled blank the finance team fills in by hand after posting the journal. */
  .blank-fill { display: inline-block; min-width: 130px; border-bottom: 1px solid #000; }
  .separator { border-top: 3px solid #000; margin: 10px 0; }
  .mid-info { margin-bottom: 15px; font-size: 9pt; }
  .mid-info-row { display: flex; justify-content: space-between; margin-bottom: 3px; }
  .mid-info-row span.label { font-weight: bold; }
  table { width: 100%; border-collapse: collapse; margin: 15px 0; font-size: 9pt; }
  th, td { border: 1px solid #000; padding: 6px 8px; vertical-align: top; text-align: left; }
  th { font-weight: bold; text-align: left; }
  .col-keterangan { width: 65%; }
  .col-realisasi { width: 35%; }
  .ket-tambahan { margin: 15px 0; padding: 8px 12px; font-size: 9pt; border-left: 3px solid #333; }
  .sig-container { display: flex; justify-content: space-between; margin-top: 25px; }
  .sig-box { width: 23%; border: 1px solid #000; padding: 8px; text-align: center; min-height: 80px; display: flex; flex-direction: column; justify-content: space-between; }
  .sig-box .sig-title { font-weight: bold; font-style: italic; font-size: 8pt; }
  .sig-box .sig-name { font-style: italic; font-size: 8pt; border-top: 1px solid #000; padding-top: 3px; margin-top: auto; }
  .footer-info { margin-top: 15px; font-size: 7pt; color: #555; }
  @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
`;

const rupiah = (value) => `Rp ${(Number(value) || 0).toLocaleString('id-ID')}`;

const row = (label, value, style = '') =>
  `<tr><td><strong>${label}</strong></td><td${style ? ` style="${style}"` : ''}>${value}</td></tr>`;

/** Gabung nama & jabatan jadi satu baris; kotak voucher hanya punya satu baris. */
const fmtSigner = ({ nama, jabatan }) =>
  [nama, jabatan].filter(Boolean).join(' — ');

const signature = (title, name) =>
  `<div class="sig-box"><div class="sig-title">${title}</div><div class="sig-name">${escapeHtml(name) || '______________'}</div></div>`;

function voucherShell({ title, voucherNo, jenisVoucher, tanggalStr, timestampStr, pembebanan, dibayarKepada, bankInfo, bodyRows, ketTambahan, signers }) {
  const ketTambahanHtml = ketTambahan
    ? `<div class="ket-tambahan"><strong>Keterangan Tambahan:</strong><br>${escapeHtml(ketTambahan)}</div>`
    : '';
  return `<!DOCTYPE html><html><head><title>${escapeHtml(title)}</title><style>${VOUCHER_STYLE}</style></head><body>
    <h1>VOUCHER PEMBAYARAN</h1>
    <div class="top-info">
      <div><span class="label">Jenis Voucher:</span> ${escapeHtml(jenisVoucher)}</div>
      <div>
        <div class="line"><span class="label">No. Voucher:</span> ${escapeHtml(voucherNo)}</div>
        <div class="line"><span class="label">No. Jurnal:</span> <span class="blank-fill"></span></div>
        <div class="line"><span class="label">Tanggal:</span> ${tanggalStr}</div>
      </div>
    </div>
    <div class="separator"></div>
    <div class="mid-info">
      <div class="mid-info-row">
        <div><span class="label">Pembebanan Kepada:</span> ${escapeHtml(pembebanan)}</div>
        <div><span class="label">Bank-No.Rek:</span> ${escapeHtml(bankInfo)}</div>
      </div>
      <div><strong>Dibayar Kepada:</strong> ${escapeHtml(dibayarKepada)}</div>
    </div>
    <table>
      <thead><tr><th class="col-keterangan">Keterangan</th><th class="col-realisasi">Realisasi (Rp)</th></tr></thead>
      <tbody>${bodyRows}</tbody>
    </table>
    ${ketTambahanHtml}
    <div class="sig-container">
      ${signature('Dibukukan Oleh', signers.dibukukanOleh || '')}
      ${signature('Diperiksa Oleh', signers.diperiksaOleh || '')}
      ${signature('Dibayar Oleh', signers.dibayarOleh || '')}
      ${signature('Diterima Oleh', signers.diterimaOleh || '')}
    </div>
    <div class="footer-info">${timestampStr}<br>Dokumen ini dibentuk secara otomatis</div>
  </body></html>`;
}

/**
 * Rekening tujuan yang BENAR-BENAR dibayar.
 *
 * Backend memakai `va_nomor` bila `uses_va` (lihat bank_process), jadi
 * mencetak supplier_data tanpa syarat akan menyatakan rekening yang salah
 * untuk invoice VA. Dipakai voucher satuan DAN gabungan supaya kedua
 * dokumen tidak mungkin menyebut rekening berbeda untuk invoice yang sama.
 */
function bankInfoOf(invoice) {
  const bank = invoice.supplier_data?.nama_bank || '-';
  if (invoice.uses_va) {
    return `${bank} - ${invoice.va_nomor || '-'} (VIRTUAL ACCOUNT)`;
  }
  if (!invoice.supplier_data) return '-';
  return `${bank} - ${invoice.supplier_data.nomor_rekening || '-'}`;
}

function printDates() {
  const now = new Date();
  const tanggalStr = now.toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' });
  return { tanggalStr, timestampStr: `${tanggalStr} ${now.toLocaleTimeString('id-ID')}` };
}

/**
 * Voucher pembayaran supplier.
 * `context`: { company, printedBy } — company dibaca dari master (bukan salinan
 * beku), printedBy adalah user AP yang menekan tombol cetak.
 */
export function printVoucherInvoice(invoice, keteranganTambahan = '', context = {}) {
  const a = invoiceAmounts(invoice);
  const { tanggalStr, timestampStr } = printDates();
  // Pembulatan sits below PPn: it corrects the bill, not the tax base, so the
  // rows read top-to-bottom as the arithmetic that produces Total Tagihan.
  const showDpp = a.potonganRetur > 0;
  // pphType di-escape: `row()` menyuntikkan label mentah, dan pph_type bisa
  // berisi string bebas (InvoiceFullUpdate & EditDeductionsRequest menerimanya
  // tanpa Literal). Untuk ketiga nilai yang sah hasilnya identik byte, jadi
  // cetakan yang benar tidak berubah sedikit pun.
  const pphLabel = a.pphType ? `${escapeHtml(a.pphType)} (${a.pphPercentage}%)` : '0';

  const bodyRows = [
    `<tr><td><strong>Deskripsi Pembayaran:</strong><br>Invoice ${escapeHtml(invoice.invoice_number)}${invoice.po_number ? ` (PO: ${escapeHtml(invoice.po_number)})` : ''}<br><br><strong>Tujuan Transaksi:</strong><br>Pembayaran supplier ${escapeHtml(invoice.supplier_name || '')}</td><td>${rupiah(a.subtotal)}</td></tr>`,
    a.potonganRetur > 0 ? row('Potongan / Retur:', `- ${rupiah(a.potonganRetur)}`, 'color:#c2410c') : '',
    showDpp ? row('DPP (Dasar Pengenaan Pajak)', `<strong>${rupiah(a.dpp)}</strong>`) : '',
    row(
      `PPn Masukan: ${a.hasPpn ? `[v] Ada PPn (${a.ppnPercentage || 11}%)` : '[ ] Ada PPn'}`,
      rupiah(a.ppnAmount)
    ),
    a.biayaAngkut > 0 ? row('Biaya Angkut:', rupiah(a.biayaAngkut)) : '',
    a.pembulatan !== 0 ? row('Pembulatan:', rupiah(a.pembulatan)) : '',
    a.dpAmount > 0 ? row(`Down Payment (${escapeHtml(invoice.dp_number || 'DP')}):`, `- ${rupiah(a.dpAmount)}`, 'color:green') : '',
    row('Total Tagihan', `<strong>${rupiah(a.totalTagihan)}</strong>`),
    row(`Potongan PPh 21/23/4(2): ${pphLabel}`, `- ${rupiah(a.pphAmount)}`, 'color:#be123c'),
    a.adminBank > 0 ? row('Admin Bank', `- ${rupiah(a.adminBank)}`) : '',
    row('Total Dibayarkan', `<strong>${rupiah(a.nilaiDibayarkan)}</strong>`),
  ].join('');

  // true = jendela cetak terbuka; false = popup diblokir (pemanggil memberi tahu user).
  return printHtml(voucherShell({
    title: `Voucher Pembayaran - ${invoice.invoice_number}`,
    // The voucher carries its own number, issued at submit-to-payment. Invoices
    // that never reached payment have none yet.
    voucherNo: invoice.voucher_number || '-',
    jenisVoucher: 'PEMBAYARAN SUPPLIER',
    tanggalStr,
    timestampStr,
    pembebanan: invoice.company_name || '-',
    dibayarKepada: invoice.supplier_name || '-',
    bankInfo: bankInfoOf(invoice),
    bodyRows,
    ketTambahan: keteranganTambahan,
    signers: {
      dibukukanOleh: context.printedBy || '',
      diperiksaOleh: fmtSigner(signatory(context.company, 'pemeriksa')),
      dibayarOleh: fmtSigner(signatory(context.company, 'finance')),
      diterimaOleh: invoice.supplier_name || '',
    },
  }));
}

/**
 * Voucher pembayaran pengajuan Internal (IPR) maupun Operasional (OPR).
 * "Dibukukan Oleh" sengaja dikosongkan — pengajuan internal tidak melewati AP.
 */
export function printVoucherInternal(request, keteranganTambahan = '', context = {}) {
  const { tanggalStr, timestampStr } = printDates();
  const totalTagihan = Number(request.amount) || 0;
  const pphAmount = Number(request.pph_amount) || 0;
  const adminBank = Number(request.admin_bank) || 0;
  // Di-escape karena `row()` menyuntikkan label mentah; lihat catatan di
  // printVoucherInvoice.
  const pphLabel = request.pph_type ? `${escapeHtml(request.pph_type)} (${request.pph_percentage || 0}%)` : '0';

  const bodyRows = [
    `<tr><td><strong>Tipe Request:</strong> ${escapeHtml(request.tipe_request || '-')}<br>${request.departemen_nama ? `<strong>Departemen:</strong> ${escapeHtml(request.departemen_nama)}<br>` : ''}${request.keterangan ? `<strong>Keterangan:</strong> ${escapeHtml(request.keterangan)}<br>` : ''}<br><strong>Penerima:</strong> ${escapeHtml(request.penerima_nama || '-')}</td><td>${rupiah(totalTagihan)}</td></tr>`,
    row('Total Tagihan', `<strong>${rupiah(totalTagihan)}</strong>`),
    row(`Potongan PPh 21/23/4(2): ${pphLabel}`, `- ${rupiah(pphAmount)}`, 'color:#be123c'),
    adminBank > 0 ? row('Admin Bank', `- ${rupiah(adminBank)}`) : '',
    row('Total Dibayarkan', `<strong>${rupiah(totalTagihan - pphAmount - adminBank)}</strong>`),
  ].join('');

  return printHtml(voucherShell({
    title: `Voucher Pembayaran - ${request.request_number}`,
    voucherNo: request.request_number || '-',
    jenisVoucher: request.tipe_request || 'INTERNAL',
    tanggalStr,
    timestampStr,
    pembebanan: request.company_name || '-',
    dibayarKepada: request.penerima_nama || '-',
    bankInfo: request.penerima_bank ? `${request.penerima_bank} - ${request.penerima_rekening || '-'}` : '-',
    bodyRows,
    ketTambahan: keteranganTambahan,
    signers: {
      dibukukanOleh: '',
      diperiksaOleh: fmtSigner(signatory(context.company, 'pemeriksa')),
      dibayarOleh: fmtSigner(signatory(context.company, 'finance')),
      diterimaOleh: request.penerima_nama || '',
    },
  }));
}

/**
 * Voucher pembayaran Down Payment supplier.
 * Rekening tujuan sama dengan yang dipakai file export bank untuk DP
 * (rekening supplier). Tanpa PPh: DP dipotong penuh di invoice final dan PPh
 * dihitung di sana; yang dikurangkan hanya admin bank.
 */
export function printVoucherDP(dp, keteranganTambahan = '', context = {}) {
  const { tanggalStr, timestampStr } = printDates();
  const nilai = Number(dp.amount) || 0;
  const adminBank = Number(dp.admin_bank) || 0;
  const sup = dp.supplier_data || {};
  const bodyRows = [
    `<tr><td><strong>Deskripsi Pembayaran:</strong><br>Down Payment ${escapeHtml(dp.dp_number || '-')} untuk PO ${escapeHtml(dp.po_number || '-')}`
      + `${dp.total_po ? ` (Total PO ${rupiah(dp.total_po)})` : ''}`
      + `${dp.keterangan ? `<br><strong>Keterangan:</strong> ${escapeHtml(dp.keterangan)}` : ''}`
      + `<br><br><strong>Tujuan Transaksi:</strong><br>Uang muka supplier ${escapeHtml(dp.supplier_name || '')}</td><td>${rupiah(nilai)}</td></tr>`,
    row('Total Tagihan', `<strong>${rupiah(nilai)}</strong>`),
    adminBank > 0 ? row('Admin Bank', `- ${rupiah(adminBank)}`) : '',
    row('Total Dibayarkan', `<strong>${rupiah(nilai - adminBank)}</strong>`),
  ].join('');

  return printHtml(voucherShell({
    title: `Voucher Pembayaran - ${dp.dp_number}`,
    voucherNo: dp.dp_number || '-',
    jenisVoucher: 'DOWN PAYMENT',
    tanggalStr,
    timestampStr,
    pembebanan: dp.company_name || '-',
    dibayarKepada: dp.supplier_name || '-',
    bankInfo: sup.nomor_rekening ? `${sup.nama_bank || '-'} - ${sup.nomor_rekening}` : '-',
    bodyRows,
    ketTambahan: keteranganTambahan,
    signers: {
      dibukukanOleh: context.printedBy || '',
      diperiksaOleh: fmtSigner(signatory(context.company, 'pemeriksa')),
      dibayarOleh: fmtSigner(signatory(context.company, 'finance')),
      diterimaOleh: dp.supplier_name || '',
    },
  }));
}

/* ===================================================================
 * VOUCHER PEMBAYARAN GABUNGAN — 2-5 invoice satu supplier, satu lembar
 *
 * `voucherShell` dipakai ulang TANPA diubah, dan body-nya memakai `row()` +
 * `rupiah()` yang SAMA dengan voucher satuan — itulah yang menjamin kedua
 * cetakan tidak mungkin berbeda angka.
 *
 * Bentuknya SATU RANTAI AGREGAT dengan urutan dan kondisi tampil yang persis
 * mencerminkan printVoucherInvoice, bukan blok rincian per invoice. Versi
 * sebelumnya mencetak 11 baris per anggota plus dua seksi rekap — 60 baris
 * untuk 5 invoice, pecah ke halaman kedua tanpa <thead> berulang. Rincian
 * per-invoice memang sudah tersedia lengkap di voucher satuan masing-masing,
 * karena tiap anggota punya voucher_number sendiri.
 *
 * Yang tersisa per anggota hanya dua baris TEKS di dalam sel, bukan <tr>:
 * identitasnya di Deskripsi Pembayaran, dan alokasi nominalnya di bawah Total
 * Dibayarkan — tepat di bawah angka yang dipecahnya. Jumlah <tr> karena itu
 * tetap sama untuk 2 maupun 5 anggota.
 *
 * Perataan rincian memakai inline style, BUKAN aturan baru di VOUCHER_STYLE,
 * supaya HTML voucher satuan tetap identik byte — bukan sekadar identik
 * render.
 *
 * PPn dan PPh direkap per (jenis, tarif): PPh23 dan PPh4(2) masuk baris SPT
 * yang berbeda, dan dua tarif PPh23 pun dilaporkan terpisah, jadi meleburnya
 * jadi satu baris menghilangkan tarif dari cetakan dan menghasilkan angka
 * yang tidak bisa dicocokkan ke satu baris SPT.
 * =================================================================== */

/** Satu baris identitas invoice di dalam sel Deskripsi Pembayaran. */
function barisIdentitas(index, inv, tandaPengurang) {
  return `<div>${[
    `${index}. ${escapeHtml(inv.invoice_number || '-')}`,
    inv.po_number ? ` (PO: ${escapeHtml(inv.po_number)})` : '',
    inv.voucher_number ? ` &middot; ${escapeHtml(inv.voucher_number)}` : '',
    tandaPengurang ? ' <strong style="color:#be123c">[PENGURANG]</strong>' : '',
  ].join('')}</div>`;
}

/** Satu baris alokasi nominal di bawah Total Dibayarkan. */
function barisAlokasi(kiri, nilai, warna = '') {
  return `<div style="display:flex;justify-content:space-between;gap:10px${warna ? `;color:${warna}` : ''}">`
    + `<span>${kiri}</span><span>${rupiah(nilai)}</span></div>`;
}

/**
 * HTML voucher gabungan sebagai STRING MURNI.
 *
 * Dipisah dari fungsi yang memanggil window.open supaya bisa di-assert di
 * unit test tanpa DOM — termasuk untuk membuktikan setiap nilai ter-escape.
 *
 * `group` berasal dari respons endpoint stempel:
 *   { voucher_gabungan_label, members, member_numbers, count, print_count }
 */
export function buildVoucherGabunganHtml(invoices, keteranganTambahan = '', context = {}, group = {}) {
  const T = batchTotals(invoices);
  const { tanggalStr, timestampStr } = printDates();
  const head = T.rows[0]?.inv || {};

  // --- sel 1: identitas tiap invoice, lalu tujuan transaksi ---
  const daftarInvoice = T.rows
    .map((r, i) => barisIdentitas(i + 1, r.inv, r.a.nilaiDibayarkan <= 0))
    .join('');
  const deskripsi = `<strong>Deskripsi Pembayaran:</strong>`
    + `<div style="margin:2px 0 8px 10px">${daftarInvoice}</div>`
    + `<strong>Tujuan Transaksi:</strong><br>`
    + `Pembayaran supplier ${escapeHtml(head.supplier_name || '')} (${T.rows.length} invoice)`;

  // --- sel terakhir: alokasi per invoice menempel di bawah totalnya ---
  const alokasi = T.rows
    .map((r) => barisAlokasi(
      `${escapeHtml(r.inv.voucher_number || '-')} / ${escapeHtml(r.inv.invoice_number || '-')}`,
      r.a.nilaiDibayarkan
    ))
    .join('')
    + (T.penyesuaianRincian !== 0
      ? barisAlokasi('Penyesuaian pembulatan tampilan', T.penyesuaianRincian, '#7c3aed')
      : '');
  const selTotal = `<strong>Total Dibayarkan</strong>`
    + `<div style="margin:4px 0 0 10px;font-size:8pt;color:#334155">${alokasi}</div>`;

  // Tanpa PPn sama sekali, barisnya tetap dicetak bernilai 0 — paritas penuh
  // dengan voucher satuan, yang juga selalu mencetak baris PPn Masukan.
  const barisPpn = T.ppnByRate.length
    ? T.ppnByRate
      .map((x) => row(`PPn Masukan: [v] Ada PPn (${escapeHtml(String(x.percentage))}%)`, rupiah(x.amount)))
      .join('')
    : row('PPn Masukan: [ ] Ada PPn', rupiah(0));

  // escapeHtml wajib: label memuat pph_type yang masih string bebas di
  // InvoiceFullUpdate dan EditDeductionsRequest, dan row() menyuntik mentah.
  const barisPph = T.pphByKey.length
    ? T.pphByKey
      .map((x) => row(`Potongan PPh 21/23/4(2): ${escapeHtml(x.label)}`, `- ${rupiah(x.amount)}`, 'color:#be123c'))
      .join('')
    : row('Potongan PPh 21/23/4(2): 0', `- ${rupiah(0)}`, 'color:#be123c');

  // Satu baris DP agregat, tapi nomornya tetap disebut — maksimal 5 anggota
  // sehingga daftarnya selalu pendek.
  const nomorDp = [...new Set(
    T.rows.filter((r) => r.a.dpAmount > 0).map((r) => String(r.inv.dp_number ?? '').trim())
  )].filter(Boolean);
  const labelDp = `Down Payment${nomorDp.length ? ` (${escapeHtml(nomorDp.join(', '))})` : ''}:`;

  // Baris penyesuaian hanya muncul bila rantai yang tercetak tidak menjumlah
  // ke total yang tercetak. Pada angka rupiah nyata tidak satu pun muncul.
  const penyesuaian = (nilai) =>
    nilai !== 0 ? row('Penyesuaian pembulatan tampilan', rupiah(nilai), 'color:#7c3aed') : '';

  const bodyRows = [
    `<tr><td>${deskripsi}</td><td>${rupiah(T.subtotal)}</td></tr>`,
    T.potonganRetur > 0 ? row('Potongan / Retur:', `- ${rupiah(T.potonganRetur)}`, 'color:#c2410c') : '',
    penyesuaian(T.penyesuaianDpp),
    T.potonganRetur > 0 ? row('DPP (Dasar Pengenaan Pajak)', `<strong>${rupiah(T.dpp)}</strong>`) : '',
    barisPpn,
    T.biayaAngkut > 0 ? row('Biaya Angkut:', rupiah(T.biayaAngkut)) : '',
    T.pembulatan !== 0 ? row('Pembulatan:', rupiah(T.pembulatan)) : '',
    T.dpAmount > 0 ? row(labelDp, `- ${rupiah(T.dpAmount)}`, 'color:green') : '',
    penyesuaian(T.penyesuaianTagihan),
    row('Total Tagihan', `<strong>${rupiah(T.totalTagihan)}</strong>`),
    barisPph,
    T.adminBank > 0 ? row('Admin Bank', `- ${rupiah(T.adminBank)}`) : '',
    penyesuaian(T.penyesuaianBayar),
    `<tr><td>${selTotal}</td><td><strong>${rupiah(T.totalDibayarkan)}</strong></td></tr>`,
  ].join('');

  // Dipisah koma + SPASI, bukan koma saja: satu token tak terpatahkan akan
  // mendorong blok kanan .top-info keluar halaman (flex tanpa min-width:0).
  const nomorAnggota = (group.member_numbers && group.member_numbers.length
    ? group.member_numbers
    : T.rows.map((r) => r.inv.voucher_number)
  ).filter(Boolean).join(', ');

  const jenis = group.voucher_gabungan_label
    ? `PEMBAYARAN SUPPLIER (GABUNGAN ${group.voucher_gabungan_label})`
    : 'PEMBAYARAN SUPPLIER (GABUNGAN)';

  return voucherShell({
    title: `Voucher Pembayaran Gabungan - ${head.supplier_name || '-'}`,
    voucherNo: nomorAnggota || '-',
    jenisVoucher: jenis,
    tanggalStr,
    timestampStr,
    pembebanan: head.company_name || '-',
    dibayarKepada: head.supplier_name || '-',
    bankInfo: bankInfoOf(head),
    bodyRows,
    ketTambahan: keteranganTambahan,
    signers: {
      dibukukanOleh: context.printedBy || '',
      diperiksaOleh: fmtSigner(signatory(context.company, 'pemeriksa')),
      dibayarOleh: fmtSigner(signatory(context.company, 'finance')),
      diterimaOleh: head.supplier_name || '',
    },
  });
}

/**
 * Cetak voucher gabungan ke jendela yang SUDAH dibuka.
 *
 * `printWindow` wajib dibuka sinkron di dalam click handler sebelum request
 * stempel: window.open() setelah await diblokir Safari dan Firefox default
 * karena sudah keluar dari call-stack gesture user. Urutannya tetap
 * stempel-dulu-cetak-kemudian; jendelanya saja yang disiapkan lebih awal.
 */
export function printVoucherInvoiceGabungan(printWindow, invoices, keteranganTambahan = '', context = {}, group = {}) {
  if (!printWindow) return { ok: false, error: 'POPUP_BLOCKED' };
  const html = buildVoucherGabunganHtml(invoices, keteranganTambahan, context, group);
  writePrintDocument(printWindow, html);
  printWhenReady(printWindow);
  return { ok: true };
}
